// Chat Header component for Inbox v2 (PRD §1.7, WP-04).
// Inline contact name edit, in-thread search, follow-up stopwatch, "Bot reply paused" badge, ⋮ options menu.
import { useState, useRef, useEffect } from 'react';
import { useI18n } from '../../lib/i18n';
import { cn } from '../../lib/cn';
import { updateConversation, deleteConversation } from '../../lib/inbox';
import { useTeam } from '../../lib/team';
import { use24hWindow } from '../../lib/use24hWindow';
import type { LiveConversation } from '../../lib/inboxTypes';
import { useToast } from '../ui/Toast';
import { useConfirm } from '../ui/ConfirmDialog';
import {
  SearchIcon,
  ClockIcon,
  MoreVerticalIcon,
  EditIcon,
  CheckIcon,
  CloseIcon,
  StarIcon,
  ArchiveIcon,
  BanIcon,
  CheckCircleIcon,
  UserIcon,
  TrashIcon,
} from '../icons';

interface ChatHeaderProps {
  conv: LiveConversation;
  onBack: () => void;
  onInfo: () => void;
  onOpenFollowup: () => void;
  inThreadSearch: string;
  onInThreadSearchChange: (q: string) => void;
}

export default function ChatHeader({
  conv,
  onBack,
  onInfo,
  onOpenFollowup,
  inThreadSearch,
  onInThreadSearchChange,
}: ChatHeaderProps) {
  const { t } = useI18n();
  const { members } = useTeam();
  const toast = useToast();
  const confirm = useConfirm();
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(conv.displayName || conv.name);
  const [showSearch, setShowSearch] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setEditedName(conv.displayName || conv.name);
  }, [conv.displayName, conv.name]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) {
        setMenuOpen(false);
        setAssignOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handleSaveName = async () => {
    const trimmed = editedName.trim();
    setIsEditingName(false);
    if (trimmed !== (conv.displayName || conv.name)) {
      try {
        await updateConversation({
          conversationId: conv.id,
          displayName: trimmed || null,
        });
      } catch (err) {
        setEditedName(conv.displayName || conv.name);
        toast.error((err as Error).message || t('common.saveFailed'));
      }
    }
  };

  const handleToggleStar = async () => {
    setMenuOpen(false);
    try {
      await updateConversation({ conversationId: conv.id, starred: !conv.starred });
    } catch (err) {
      toast.error((err as Error).message || t('common.saveFailed'));
    }
  };

  const handleToggleArchive = async () => {
    setMenuOpen(false);
    try {
      await updateConversation({ conversationId: conv.id, archived: !conv.archived });
    } catch (err) {
      toast.error((err as Error).message || t('common.saveFailed'));
    }
  };

  const handleToggleBlock = async () => {
    setMenuOpen(false);
    const next = !conv.blocked;
    if (next && !(await confirm({ title: t('inbox.block'), body: t('inbox.blockConfirm'), confirmLabel: t('inbox.block'), danger: true }))) {
      return;
    }
    try {
      await updateConversation({ conversationId: conv.id, blocked: next });
    } catch (err) {
      toast.error((err as Error).message || t('common.saveFailed'));
    }
  };

  const handleToggleResolve = async () => {
    setMenuOpen(false);
    const nextStatus = conv.status === 'resolved' ? 'open' : 'resolved';
    try {
      await updateConversation({ conversationId: conv.id, status: nextStatus });
    } catch (err) {
      toast.error((err as Error).message || t('common.saveFailed'));
    }
  };

  const handleMarkUnread = async () => {
    setMenuOpen(false);
    try {
      await updateConversation({ conversationId: conv.id, unreadCount: 1, unread: true });
    } catch (err) {
      toast.error((err as Error).message || t('common.saveFailed'));
    }
  };

  const handleAssign = async (memberUid: string | null) => {
    setMenuOpen(false);
    setAssignOpen(false);
    try {
      await updateConversation({ conversationId: conv.id, assignedTo: memberUid, assignee: memberUid });
    } catch (err) {
      toast.error((err as Error).message || t('common.saveFailed'));
    }
  };

  const handleDelete = async () => {
    setMenuOpen(false);
    if (!(await confirm({ title: t('inbox.delete'), body: t('inbox.deleteConfirm'), danger: true }))) return;
    try {
      await deleteConversation({ conversationId: conv.id });
      onBack();
    } catch (err) {
      toast.error((err as Error).message || t('common.saveFailed'));
    }
  };

  const windowState = use24hWindow(conv);
  const isBotPaused = Boolean(conv.botPaused || !conv.botReply);

  return (
    <div className="border-b border-line bg-white px-3 sm:px-4 py-2.5 dark:border-slate-800 dark:bg-slate-900 transition-colors">
      <div className="flex items-center justify-between gap-2.5">
        {/* Contact Info & Back */}
        <div className="flex min-w-0 items-center gap-2.5">
          <button
            type="button"
            onClick={onBack}
            aria-label={t('inbox.back') || 'Back'}
            className="grid h-8 w-8 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 md:hidden"
          >
            ‹
          </button>

          <span
            className={cn(
              'grid h-9 w-9 shrink-0 place-items-center rounded-full text-xs font-bold text-white shadow-sm',
              conv.avatarColor,
            )}
            aria-hidden
          >
            {conv.initials}
          </span>

          <div className="min-w-0 flex-1">
            {isEditingName ? (
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  autoFocus
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void handleSaveName();
                    if (e.key === 'Escape') setIsEditingName(false);
                  }}
                  onBlur={() => void handleSaveName()}
                  className="rounded border border-brand bg-surface px-2 py-0.5 text-xs font-bold text-ink outline-none dark:bg-slate-800 dark:text-slate-100"
                />
                <button
                  type="button"
                  onClick={() => void handleSaveName()}
                  className="text-emerald-600 hover:text-emerald-700"
                >
                  <CheckIcon size={14} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 group">
                <b className="truncate text-xs sm:text-sm text-ink dark:text-slate-100">
                  {conv.displayName || conv.name}
                </b>
                {conv.starred && <StarIcon size={13} className="text-amber-400 fill-amber-400 shrink-0" />}
                <button
                  type="button"
                  onClick={() => setIsEditingName(true)}
                  title={t('inbox.editName') || 'Edit name'}
                  className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-opacity"
                >
                  <EditIcon size={12} />
                </button>
              </div>
            )}

            <div className="flex items-center gap-2 truncate text-[11px] text-ink-3 dark:text-slate-400">
              <span>{conv.phone}</span>
              {conv.assignee && (
                <span className="rounded bg-slate-100 px-1.5 py-0.2 text-[10px] font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  🧑‍💼 {conv.assignee}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Header Right Controls */}
        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          {/* Bot Reply Paused alert chip */}
          {isBotPaused && (
            <span
              className="hidden lg:inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-[10px] font-bold text-amber-800 dark:bg-amber-950/50 dark:text-amber-300"
              title={t('inbox.botPausedTooltip') || 'Bot Reply is currently Paused'}
            >
              ⏸ {t('inbox.botPausedBadge') || 'Bot Paused'}
            </span>
          )}

          {/* 24h Window countdown */}
          <span
            className={cn(
              'hidden sm:inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold',
              windowState.warn
                ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400'
                : 'bg-primary-50 text-primary dark:bg-brand-950/40 dark:text-brand-tint',
            )}
          >
            ⏱ {windowState.label}
          </span>

          {/* In-Thread Search Toggle */}
          <button
            type="button"
            onClick={() => {
              setShowSearch((v) => !v);
              if (showSearch) onInThreadSearchChange('');
            }}
            title={t('inbox.searchInConversation') || 'Search in conversation'}
            className={cn(
              'grid h-8 w-8 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 transition-colors',
              showSearch && 'bg-slate-100 text-brand dark:bg-slate-800',
            )}
          >
            <SearchIcon size={16} />
          </button>

          {/* Follow-up Stopwatch Button */}
          <button
            type="button"
            onClick={onOpenFollowup}
            title={t('followup.title') || 'Follow-up'}
            className="grid h-8 w-8 place-items-center rounded-lg text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-950/40 transition-colors"
          >
            <ClockIcon size={16} />
          </button>

          {/* More Menu (⋮) */}
          <div ref={menuRef} className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={t('common.more') || 'More options'}
              className="grid h-8 w-8 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
            >
              <MoreVerticalIcon size={17} />
            </button>

            {menuOpen && (
              <div className="absolute end-0 top-full z-40 mt-1 w-52 overflow-hidden rounded-xl border border-line bg-white py-1 shadow-xl dark:border-slate-800 dark:bg-slate-900">
                {/* Star / Unstar */}
                <button
                  type="button"
                  onClick={handleToggleStar}
                  className="flex w-full items-center gap-2.5 px-3.5 py-2 text-start text-xs font-medium text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <StarIcon size={15} className={conv.starred ? 'text-amber-500 fill-amber-500' : 'text-slate-400'} />
                  <span>{conv.starred ? t('inbox.unstar') || 'Unmark Starred' : t('inbox.star') || 'Mark as Starred'}</span>
                </button>

                {/* Resolve / Reopen */}
                <button
                  type="button"
                  onClick={handleToggleResolve}
                  className="flex w-full items-center gap-2.5 px-3.5 py-2 text-start text-xs font-medium text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <CheckCircleIcon size={15} className={conv.status === 'resolved' ? 'text-emerald-500' : 'text-slate-400'} />
                  <span>{conv.status === 'resolved' ? t('inbox.reopen') || 'Reopen Conversation' : t('inbox.resolve') || 'Resolve Conversation'}</span>
                </button>

                {/* Mark as Unread */}
                <button
                  type="button"
                  onClick={handleMarkUnread}
                  className="flex w-full items-center gap-2.5 px-3.5 py-2 text-start text-xs font-medium text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <span className="text-xs">✉️</span>
                  <span>{t('inbox.markUnread') || 'Mark as Unread'}</span>
                </button>

                {/* Assign to Member */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setAssignOpen((v) => !v)}
                    className="flex w-full items-center justify-between px-3.5 py-2 text-start text-xs font-medium text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    <div className="flex items-center gap-2.5">
                      <UserIcon size={15} className="text-slate-400" />
                      <span>{t('inbox.ctx.assignTo') || 'Assign Agent'}</span>
                    </div>
                    <span className="text-[10px] text-ink-4">›</span>
                  </button>

                  {assignOpen && (
                    <div className="border-t border-b border-line bg-slate-50 py-1 dark:border-slate-800 dark:bg-slate-850">
                      <button
                        type="button"
                        onClick={() => void handleAssign(null)}
                        className="flex w-full px-5 py-1.5 text-xs text-slate-600 hover:bg-slate-200/50 dark:text-slate-300 dark:hover:bg-slate-800"
                      >
                        {t('inbox.ctx.unassigned') || 'Unassigned'}
                      </button>
                      {members.map((m) => (
                        <button
                          key={m.uid}
                          type="button"
                          onClick={() => void handleAssign(m.uid)}
                          className={cn(
                            'flex w-full px-5 py-1.5 text-xs text-slate-700 hover:bg-slate-200/50 dark:text-slate-200 dark:hover:bg-slate-800',
                            conv.assignedTo === m.uid && 'font-bold text-brand',
                          )}
                        >
                          {m.email} {m.isSelf ? '(You)' : ''}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Archive / Unarchive */}
                <button
                  type="button"
                  onClick={handleToggleArchive}
                  className="flex w-full items-center gap-2.5 px-3.5 py-2 text-start text-xs font-medium text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <ArchiveIcon size={15} className="text-slate-400" />
                  <span>{conv.archived ? t('inbox.unarchive') || 'Unarchive' : t('inbox.archive') || 'Archive'}</span>
                </button>

                {/* Block / Unblock */}
                <button
                  type="button"
                  onClick={handleToggleBlock}
                  className="flex w-full items-center gap-2.5 px-3.5 py-2 text-start text-xs font-medium text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <BanIcon size={15} className={conv.blocked ? 'text-rose-500' : 'text-slate-400'} />
                  <span>{conv.blocked ? t('inbox.unblock') || 'Unblock Contact' : t('inbox.block') || 'Block Contact'}</span>
                </button>

                {/* Delete */}
                <button
                  type="button"
                  onClick={handleDelete}
                  className="flex w-full items-center gap-2.5 border-t border-line px-3.5 py-2 text-start text-xs font-medium text-rose-600 hover:bg-rose-50 dark:border-slate-800 dark:text-rose-400 dark:hover:bg-rose-950/30"
                >
                  <TrashIcon size={15} />
                  <span>{t('inbox.delete') || 'Delete Conversation'}</span>
                </button>
              </div>
            )}
          </div>

          {/* Info Panel Toggle Button */}
          <button
            type="button"
            onClick={onInfo}
            aria-label={t('inbox.ctx.info') || 'Customer details'}
            className="grid h-8 w-8 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
          >
            ⓘ
          </button>
        </div>
      </div>

      {/* In-Thread Search Input Bar */}
      {showSearch && (
        <div className="mt-2.5 flex items-center gap-2 rounded-xl border border-line bg-surface px-3 py-1.5 dark:border-slate-800 dark:bg-slate-800">
          <SearchIcon size={14} className="text-slate-400 shrink-0" />
          <input
            type="search"
            autoFocus
            value={inThreadSearch}
            onChange={(e) => onInThreadSearchChange(e.target.value)}
            placeholder={t('inbox.searchPlaceholderInThread') || 'Search messages in this thread…'}
            className="min-w-0 flex-1 bg-transparent text-xs text-ink outline-none dark:text-slate-200"
          />
          {inThreadSearch && (
            <button
              type="button"
              onClick={() => onInThreadSearchChange('')}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
            >
              <CloseIcon size={14} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
